import { state } from './state.js';
import * as UI from './ui.js';
import * as Presets from './presets.js';
import * as API from './api.js';
import { startJobController, cancelJobController } from './job_controller.js';
import { UI_COPY } from './config/uiCopy.js';
import { openCreateMosaicForEdit, refreshSettingsVisibility, requestWorkflowScroll, setSettingsOverride } from './workflow_selection.js';
import { handleLibSelect } from './library.js';
import { isAnyModalOpen, openCreateLibraryModal } from './modal_manager.js';
import {
    clearSelectedSourcePhoto,
    selectSourcePhotoById,
    selectSourcePhotoByKey,
    selectSourcePhotoByUrl,
    setSelectedSourceFromUpload,
} from './source_photos.js';

function isTextEntryContext(target) {
    if (!target) return false;
    if (target.isContentEditable) return true;
    return Boolean(target.closest('input, textarea, select, [contenteditable="true"]'));
}

function revealCreateFlowAndScroll(targetCard, source, options = {}) {
    openCreateMosaicForEdit({
        forceSettings: options.forceSettings === true,
        step: options.step || '',
        source,
        pushHistory: true,
    });
    window.requestAnimationFrame(() => {
        requestWorkflowScroll(targetCard, {
            source,
            afterFrame: true,
            delayMs: 80,
            pushHistory: true,
        });
    });
}

function initKeyboardShortcuts() {
    document.addEventListener('keydown', async (e) => {
        if (e.defaultPrevented || e.repeat) return;
        if (e.metaKey || e.ctrlKey || e.altKey) return;
        if (isTextEntryContext(e.target)) return;
        if (isAnyModalOpen()) return;

        const zoomModal = document.getElementById('zoomModal');
        if (zoomModal && !zoomModal.classList.contains('hidden')) return;

        const key = String(e.key || '').toLowerCase();
        if (!key) return;

        if (key === 'u') {
            const targetFile = document.getElementById('targetFile');
            if (targetFile) {
                e.preventDefault();
                targetFile.click();
            }
            return;
        }

        if (key === 'l') {
            e.preventDefault();
            if (!state.selectedLibraryId) return;
            const libFiles = document.getElementById('libFiles');
            if (libFiles) libFiles.click();
            return;
        }

        if (key === 'n') {
            e.preventDefault();
            const res = await openCreateLibraryModal();
            if (res && res.name) await handleLibSelect(null, res.name, res.isPublic);
            return;
        }

        if (key === 'g') {
            const generateBtn = document.getElementById('generateBtn');
            if (!generateBtn || generateBtn.disabled) {
                e.preventDefault();
                UI.showToast('Action needed', 'Select a main photo and a tile library first.', 2600);
                return;
            }
            e.preventDefault();
            await startJobController();
        }
    });
}

export function initControlHandlers() {
    const appHomeLink = document.getElementById('appHomeLink');
    if (appHomeLink) {
        appHomeLink.addEventListener('click', (event) => {
            event.preventDefault();
            requestWorkflowScroll('history', {
                source: 'brand_home_click',
                pushHistory: true,
            });
        });
    }

    document.getElementById('generateBtn').addEventListener('click', startJobController);
    const collapsedGenerateBtn = document.getElementById('settingsCollapsedGenerateBtn');
    if (collapsedGenerateBtn) {
        collapsedGenerateBtn.addEventListener('click', () => {
            if (collapsedGenerateBtn.disabled) return;
            startJobController();
        });
    }
    initKeyboardShortcuts();

    const stopBtn = document.getElementById('stopBtn');
    if (stopBtn) stopBtn.addEventListener('click', cancelJobController);

    const inspectBtn = document.getElementById('inspectBtn');
    if (inspectBtn) {
        if (!state.isAdmin) {
            const inspectWrap = inspectBtn.closest('.btn-tooltip') || inspectBtn;
            inspectWrap.classList.add('hidden');
        }
        inspectBtn.addEventListener('click', () => {
            if (!state.isAdmin) return;
            UI.toggleModal(true);
            API.logUiFlow('result', 'inspect_open', 'ok', {
                job_id: state.selectedHistoryJobId || state.jobId || '',
            }).catch(() => {});
        });
    }

    const reuseSettingsBtn = document.getElementById('reuseSettingsBtn');
    if (reuseSettingsBtn) {
        reuseSettingsBtn.addEventListener('click', () => {
            if (!state.lastShownSettings) {
                UI.showToast('No settings loaded', UI_COPY.alerts.noSettingsLoaded, 2600);
                return;
            }
            Presets.applySettings(state.lastShownSettings);
            setSettingsOverride(true);
            refreshSettingsVisibility();
            revealCreateFlowAndScroll('settings', 'reuse_settings', { forceSettings: true });
            API.logEvent('use_same_settings_click', {
                mosaic_job_id: state.selectedHistoryJobId || state.jobId || '',
            }).catch(() => {});
        });
    }

    const reuseSourceBtn = document.getElementById('reuseSourceBtn');
    if (reuseSourceBtn) {
        reuseSourceBtn.addEventListener('click', async () => {
            const info = state.lastShownSourceInfo || null;
            API.logUiFlow('main_photo', 'reuse_source', 'attempt', {
                mosaic_job_id: state.selectedHistoryJobId || state.jobId || '',
                has_source_photo_id: Boolean(info && info.sourcePhotoId),
                has_source_key: Boolean(info && info.sourceKey),
                has_canonical_source_key: Boolean(info && info.canonicalSourceKey),
                has_source_url: Boolean(info && info.sourceUrl),
            }).catch(() => {});
            if (!info || (!info.sourcePhotoId && !info.sourceUrl && !info.sourceKey && !info.canonicalSourceKey)) {
                UI.showToast('No source photo found', 'Open a mosaic first to reuse its source photo.', 2600);
                API.logUiFlow('main_photo', 'reuse_source', 'blocked', {
                    reason: 'missing_source_context',
                    mosaic_job_id: state.selectedHistoryJobId || state.jobId || '',
                }).catch(() => {});
                return;
            }
            const finishReuse = (matchedBy) => {
                refreshSettingsVisibility();
                revealCreateFlowAndScroll('main', 'reuse_source');
                API.logEvent('use_same_source_click', {
                    mosaic_job_id: state.selectedHistoryJobId || state.jobId || '',
                    matched_by: matchedBy || 'unknown',
                }).catch(() => {});
                API.logUiFlow('main_photo', 'reuse_source', 'ok', {
                    mosaic_job_id: state.selectedHistoryJobId || state.jobId || '',
                    matched_by: matchedBy || 'unknown',
                }).catch(() => {});
            };
            if (info.sourcePhotoId && selectSourcePhotoById(info.sourcePhotoId)) {
                finishReuse('source_photo_id');
                return;
            }
            if (info.canonicalSourceKey && selectSourcePhotoByKey(info.canonicalSourceKey)) {
                finishReuse('canonical_source_key');
                return;
            }
            if (info.sourceKey && selectSourcePhotoByKey(info.sourceKey)) {
                finishReuse('source_key');
                return;
            }
            if (info.sourceUrl && selectSourcePhotoByUrl(info.sourceUrl)) {
                finishReuse('source_url');
                return;
            }
            let fetchFailed = false;
            if (info.sourcePhotoId) {
                try {
                    const fetched = await API.getSourcePhoto(info.sourcePhotoId);
                    if (fetched && fetched.id) {
                        setSelectedSourceFromUpload(fetched, { jobId: info.jobId || '' });
                        finishReuse('source_photo_api');
                        return;
                    }
                } catch (e) {
                    fetchFailed = true;
                }
            }
            UI.showToast(
                'Source photo unavailable',
                'Source photo was deleted from Main Photos. Reupload to use it again.',
                3200
            );
            API.logUiFlow('main_photo', 'reuse_source', 'lookup_miss', {
                mosaic_job_id: state.selectedHistoryJobId || state.jobId || '',
                has_source_photo_id: Boolean(info.sourcePhotoId),
                has_source_key: Boolean(info.sourceKey),
                has_canonical_source_key: Boolean(info.canonicalSourceKey),
                has_source_url: Boolean(info.sourceUrl),
                fetch_failed: fetchFailed,
            }).catch(() => {});
        });
    }

    const createAnotherBtn = document.getElementById('createAnotherBtn');
    if (createAnotherBtn) {
        createAnotherBtn.addEventListener('click', () => {
            revealCreateFlowAndScroll('main', 'create_another', { step: 'main' });
            UI.showToast('Create another mosaic', 'Choose source and settings for your next variation.', 2800);
            API.logUiFlow('result', 'create_another', 'ok', {
                mosaic_job_id: state.selectedHistoryJobId || state.jobId || '',
            }).catch(() => {});
        });
    }

    const newSourceBtn = document.getElementById('newSourceBtn');
    if (newSourceBtn) {
        newSourceBtn.addEventListener('click', () => {
            clearSelectedSourcePhoto();
            refreshSettingsVisibility();
            revealCreateFlowAndScroll('main', 'reuse_new_source');
            API.logUiFlow('main_photo', 'reuse_new_source', 'ok', {
                mosaic_job_id: state.selectedHistoryJobId || state.jobId || '',
            }).catch(() => {});
        });
    }

    const zoomCloseBtn = document.getElementById('zoomCloseBtn');
    if (zoomCloseBtn) zoomCloseBtn.addEventListener('click', () => UI.toggleModal(false));

}
